import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/axiosInstance";
import CarrierHeader from "../../components/CarrierHeader";

const ReportIssues = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [segment, setSegment] = useState("other");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Segment options for dropdown
  const segmentOptions = [
    { value: "Dashboard", label: "Dashboard"},
    {
      value: "Adding Manager",
      label: "Adding Manager",
    },
    { value: "Communication", label: "Communication"},
    {
      value: "Passwords",
      label: "Passwords",
    },
    { value: "Profile", label: "Profile" },
    { value: "Packages", label: "Packages" },
    { value: "Payments", label: "Payments" },
    { value: "Drivers", label: "Drivers" },
    { value: "Carriers", label: "Carriers" },
  ];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage("File size must be less than 10MB");
      setImage(null);
      setImagePreview(null);
      return;
    }

    // Validate file type - accept images, PDFs, and documents
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validTypes.includes(file.type)) {
      setErrorMessage(
        "Please select a valid file (images, PDF, or Word documents)"
      );
      setImage(null);
      setImagePreview(null);
      return;
    }

    setImage(file);

    // Preview only for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }

    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Validation
    if (!title.trim()) {
      setErrorMessage("Please enter a title");
      return;
    }
    if (!description.trim()) {
      setErrorMessage("Please enter a description");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      const carrier = JSON.parse(localStorage.getItem("carrierUser"));

      // Append required fields
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      formData.append("segment", segment);
      formData.append("carrierId", carrier?._id || "anonymous");

      // Append file if exists
      if (image) {
        formData.append("attachment", image);
      }

      const response = await api.post("/problem-reports", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Report created:", response.data);

      // Show success alert and stay on page
      window.alert(
        "Issue reported successfully! Our team will review it shortly."
      );

      // Reset form after clicking OK on alert
      setTitle("");
      setDescription("");
      setSegment("other");
      setImage(null);
      setImagePreview(null);

      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = "";
      }
    } catch (err) {
      console.error("Error reporting issue:", err);
      setErrorMessage(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to report issue. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex bg-light-gray min-h-screen">
      <CarrierHeader />

      {/* Main Content */}
      <div className="flex-1 sm:ml-64 p-4 sm:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-dark mb-2">
              Report a Problem
            </h1>
            <p className="text-gray text-lg">
              Help us improve by reporting any issues you encounter
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
            {/* Error Message */}
            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 font-semibold flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  {errorMessage}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Segment Dropdown */}
              <div>
                <label className="block text-dark font-semibold mb-2 text-lg">
                  Issue Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                  disabled={loading}
                >
                  {segmentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-gray text-sm mt-1">
                  Select the category that best describes your issue
                </p>
              </div>

              {/* Title Field */}
              <div>
                <label className="block text-dark font-semibold mb-2 text-lg">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief title of the issue"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                  disabled={loading}
                  maxLength={100}
                />
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-dark font-semibold mb-2 text-lg">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the issue in detail. What were you trying to do? What went wrong? Include tracking numbers, dates, or any relevant information."
                  rows="6"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition resize-none"
                  disabled={loading}
                />
                <p className="text-gray text-sm mt-1">
                  {description.length} characters
                </p>
              </div>

              {/* File Upload Field */}
              <div>
                <label className="block text-dark font-semibold mb-2 text-lg">
                  Upload Attachment{" "}
                  <span className="text-gray text-sm font-normal">
                    (Optional)
                  </span>
                </label>
                <p className="text-gray text-sm mb-3">
                  Maximum file size: 10MB. Supported formats: JPG, PNG, GIF,
                  WebP, PDF, Word documents
                </p>

                {/* Image Preview */}
                {imagePreview && (
                  <div className="mb-4 relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-64 rounded-lg border border-border"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                      disabled={loading}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}

                {/* File Info for non-image files */}
                {image && !imagePreview && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="fas fa-file-alt text-blue-600 text-2xl"></i>
                      <div>
                        <p className="font-semibold text-dark">{image.name}</p>
                        <p className="text-sm text-gray">
                          {(image.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                      className="text-red-500 hover:text-red-700"
                      disabled={loading}
                    >
                      <i className="fas fa-times-circle text-xl"></i>
                    </button>
                  </div>
                )}

                {/* File Input */}
                <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-primary rounded-lg cursor-pointer hover:bg-blue-50 transition">
                  <div className="text-center">
                    <i className="fas fa-cloud-upload-alt text-primary text-3xl mb-2 block"></i>
                    <p className="text-dark font-semibold">
                      {image
                        ? "Change Attachment"
                        : "Click to upload or drag and drop"}
                    </p>
                    <p className="text-gray text-sm mt-1">
                      {image
                        ? image.name
                        : "Images, PDF, Word documents up to 10MB"}
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleImageChange}
                    className="hidden"
                    disabled={loading}
                  />
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Submit Report
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/carrier/dashboard")}
                  disabled={loading}
                  className="flex-1 bg-gray hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportIssues;
